import React, { useState } from "react";

import { useDeleteDag } from "api/api-hooks/airflow-hooks";
import { redirectToAirflowHomeUrl } from "api/ScheduledTasksApi";
import ActionButtons from "components/basic/action-buttons/ActionButtons";
import CollapsibleText from "components/basic/collapsible-text/CollapsibleText";
import DataTable from "components/basic/data-table/DataTable";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import { Button } from "primereact/button";
import { DataTableProps } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { T } from "services/lang/LanguageService";
import { useUserState } from "store/user-store";
import { AirflowDag } from "types/airflow";
import AirflowDagForm from "./AirflowDagForm";
import SearchUsage from "./SearchUsage";

type AirflowDagListProps = {
  data: AirflowDag[];
  loading?: boolean;
};

const AirflowDagList: React.FC<AirflowDagListProps> = ({ data, loading }) => {
  const deleteDag = useDeleteDag();
  const userPermissions = useUserState(state => state.currentUser?.effectivePermissions);

  const canEdit = userPermissions?.includes("AIRFLOW_EDIT");

  const renderActions = (rowData: AirflowDag) =>  {
    return canEdit ? (
      <ActionButtons
        urlOnLink={`/airflow/${rowData.dagId}`}
        onClickDelete={() => deleteDag.mutate(rowData.dagId)}
      />
    ) : (
      <ActionButtons urlOnLink={`/airflow/${rowData.dagId}`} />
    );
  }

  const renderDagId = (rowData: AirflowDag) => {
    return (
      <CollapsibleText text={rowData.dagId} />
    )
  }

  const columns: TableColumn<AirflowDag>[] = [
    {
      field: "dagId",
      header: "Airflow ID",
      columnKey: "dagId",
      body: renderDagId
    },
    {
      field: "title",
      header: T("word.title"),
      columnKey: "title",
    },
    {
      field: "description",
      header: T("word.description"),
      columnKey: "description",
    },
    {
      field: "cronSchedule",
      header: T("word.schedule"),
      columnKey: "cronSchedule",
    },
    {
      field: "tasks",
      header: T("word.tasks-count"),
      columnKey: "tasksCount",
      style: { justifyItems: "center" },
      className: "text-center",
      body: (rowData: AirflowDag) => rowData.tasks.length
    },
    {
      header: T("word.actions"),
      columnKey: "actions",
      style: { justifyItems: "center" },
      body: renderActions,
    }
  ];

  const tableConfig: DataTableProps<AirflowDag[]> = {
    rows: 25,
    loading,
    header: <DagsHeader canEdit={canEdit} />,
  };

  return <DataTable columns={columns} data={data ?? []} tableConfig={tableConfig} />;
}

export default AirflowDagList;

const DagsHeader = ({ canEdit }: { canEdit?: boolean }) => {
  const [createDagDialog, setCreateDagDialog] = useState(false);
  const [searchTemplateDialog, setSearchTemplateDialog] = useState(false);

  return (
    <>
      <div className="flex justify-content-between px-3 py-2 flex-wrap gap-2">
        <div>
          {canEdit && (
            <Button
              label={`${T("action.create")} DAG`}
              icon="pi pi-plus"
              className="p-button-sm"
              onClick={() => setCreateDagDialog(true)}
            />
          )}
        </div>
        <div className="flex gap-2">
          <Button
            label="Search usage"
            icon="pi pi-search"
            className="p-button-sm p-button-help"
            onClick={() => setSearchTemplateDialog(true)}
          />
          <a
            href={redirectToAirflowHomeUrl}
            target="_blank"
            rel="noreferrer"
          >
            <Button type="button" label="Airflow" icon="pi pi-link" className="p-button-sm" />
          </a>
        </div>
      </div>

      <Dialog
        visible={createDagDialog}
        header="Create new DAG"
        onHide={() => setCreateDagDialog(false)}
        style={{ width: "min(100%, 700px)" }}
      >
        <AirflowDagForm closeDialog={() => setCreateDagDialog(false)} />
      </Dialog>

      <Dialog
        visible={searchTemplateDialog}
        header="Search usage"
        onHide={() => setSearchTemplateDialog(false)}
        style={{ width: "min(100%, 700px)" }}
      >
        <SearchUsage closeDialog={() => setSearchTemplateDialog(false)} />
      </Dialog>
    </>
  );
};